import React, { useEffect, useRef, useState } from "react";
import { Btn } from "./primitives";
import { Eraser } from "@phosphor-icons/react";

/**
 * SignaturePad — HTML canvas signature input.
 *
 * Props:
 *   value         : data URL (PNG) — controlled
 *   onChange(url) : called when user finishes a stroke
 *   width         : default 500
 *   height        : default 160
 *   label         : button label / placeholder
 *   testId        : data-testid prefix
 */
export default function SignaturePad({ value, onChange, width = 500, height = 160, label, testId = "sig" }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  // Keep canvas backing store at high DPI for crisp lines
  const ratio = typeof window !== "undefined" ? (window.devicePixelRatio || 1) : 1;

  // Re-paint when value changes (e.g. loading existing signature)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#1F1F1F";
    if (value) {
      const img = new window.Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        setHasInk(true);
      };
      img.src = value;
    } else {
      setHasInk(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, width, height]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: t.clientX - rect.left,
      y: t.clientY - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(x, y);
    ctx.stroke();
    last.current = { x, y };
    if (!hasInk) setHasInk(true);
  };

  const end = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    e?.preventDefault?.();
    const url = canvasRef.current.toDataURL("image/png");
    onChange?.(url);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    setHasInk(false);
    onChange?.("");
  };

  return (
    <div className="space-y-2" data-testid={`${testId}-pad`}>
      <div className="border border-[var(--border)] rounded-sm bg-white inline-block touch-none select-none">
        <canvas
          ref={canvasRef}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          className="block cursor-crosshair"
          data-testid={`${testId}-canvas`}
        />
      </div>
      <div className="flex items-center gap-3">
        <Btn type="button" variant="ghost" onClick={clear} disabled={!hasInk} data-testid={`${testId}-clear`}>
          <Eraser size={14} /> {label || "Löschen"}
        </Btn>
        <span className="text-xs text-[var(--muted-2)]">
          {hasInk ? "✓ Unterschrift erfasst" : "Mit Maus, Stift oder Finger unterschreiben"}
        </span>
      </div>
    </div>
  );
}
